import { Button, Dropdown, Input, ModalWrapper } from '@/shared';
import type { DropdownOption } from '@/shared/components/ui/Dropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { useCreateAnnouncement } from '../../hooks/useAnnouncementMutations';
import type { CreateAnnouncementPayload, PriorityType } from '../../api';
import { useHousesList } from '@/features/houses/hooks/useHouseQueries';
import { resourceApi } from '@/features/resources/api/resourceApi';
import toast from 'react-hot-toast';

// -----------------------------
// Dropdown options
// -----------------------------
const categories: DropdownOption<string>[] = [
  { label: 'Maintenance', value: 'MAINTENANCE' },
  { label: 'Community', value: 'COMMUNITY' },
  { label: 'Billing', value: 'BILLING' },
];

const priorities: DropdownOption<PriorityType>[] = [
  { label: 'Normal', value: 'NORMAL' },
  { label: 'High', value: 'HIGH' },
  { label: 'Urgent', value: 'URGENT' },
  { label: 'Low', value: 'LOW' },
];

const audiences: DropdownOption<string>[] = [
  { label: 'All Residents', value: 'all_residents' },
  { label: 'Managers', value: 'managers' },
  { label: 'Specific Houses', value: 'specific_houses' },
];

// -----------------------------
// Component
// -----------------------------
interface AddHomeAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddHomeAnnouncementModal: React.FC<
  AddHomeAnnouncementModalProps
> = ({ isOpen, onClose }) => {
  const { data: houses } = useHousesList();
  const houseOptions: DropdownOption<string>[] =
    houses?.map((house) => ({
      label: house.name,
      value: house.id,
    })) || [];

  const [announcementStatus, setAnnouncementStatus] = useState<
    'DRAFT' | 'PUBLISHED' | 'SCHEDULED'
  >('PUBLISHED');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | undefined>(
    undefined,
  );

  const {
    mutate: createAnnouncement,
    isError,
    error,
  } = useCreateAnnouncement();

  const [step, setStep] = useState<number>(1);

  // ✅ Fixed Validation Schema with conditional validation
  const schema = yup.object({
    title: yup.string().required('Title is required'),
    details: yup.string().required('Details are required'),
    category: yup.string().required('Category is required'),
    priority: yup.string().required('Priority is required'),
    file: yup.mixed<FileList>().nullable().optional(),
    audience: yup.string().required('Audience is required'),
    houses: yup
      .array()
      .of(yup.string().required())
      .when('audience', {
        is: (val: string) =>
          val === 'all_residents' || val === 'specific_houses',
        then: (schema) => schema.min(1, 'Select at least one house'),
        otherwise: (schema) => schema.optional(),
      }),
    schedule: yup.string().optional(),
  });

  // ✅ Explicitly define the form data type to match the schema
  interface HomeAnnouncementFormData {
    title: string;
    details: string;
    category: string;
    priority: PriorityType;
    file?: FileList | null;
    audience: string;
    houses: string[];
    schedule: string;
  }

  const {
    register,
    handleSubmit,
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm<HomeAnnouncementFormData>({
    resolver: yupResolver(schema) as Resolver<HomeAnnouncementFormData>, // Use type assertion to bypass the type mismatch
    defaultValues: {
      file: null, // Provide default value
      houses: [],
    },
  });

  // Watch audience to conditionally show house selector
  const selectedAudience = watch('audience');

  const stepsHeading = [
    'House Announcement Information',
    'Audience and Scheduling',
  ];

  const nextStep = async () => {
    const isValid = await trigger(['title', 'details', 'category', 'priority']);
    if (isValid) setStep(2);
  };

  const onSubmit = async (data: HomeAnnouncementFormData) => {
    // Conditionally validate houses based on audience
    const fieldsToValidate = ['audience', 'schedule'];
    if (
      data.audience === 'all_residents' ||
      data.audience === 'specific_houses'
    ) {
      fieldsToValidate.push('houses');
    }
    const isValid = await trigger(fieldsToValidate as any);
    if (!isValid) return;

    if (data.file && data.file.length > 0 && !uploadedFileUrl) {
      toast.error('Please upload the selected file before publishing');
      return;
    }

    try {
      const payload: CreateAnnouncementPayload = {
        title: data.title,
        description: data.details,
        category: data.category,
        priority: data.priority as PriorityType,
        file: uploadedFileUrl,
        audience: [data.audience],
        house: (data.houses?.filter(Boolean) as string[]) || [],
        scheduleTime: data.schedule,
        status: announcementStatus,
      };

      console.log('Final Payload:', payload);

      createAnnouncement(payload, {
        onSuccess: () => {
          onClose();
          setIsUploading(false);
          toast.success('Announcement created successfully');
        },
        onError: () => {
          setIsUploading(false);
          toast.error('Failed to create announcement');
        },
      });
    } catch (error) {
      console.error('Error creating announcement:', error);
      setIsUploading(false);
      toast.error('Failed to upload file or create announcement');
    }
  };

  const handleFileUpload = async () => {
    const fileList = watch('file');
    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];
    setIsUploading(true);

    try {
      // 1. Get Signed URL
      const { signedUrl, path } = await resourceApi.getUploadUrl(
        file.name,
        'announcements',
        file.type,
      );

      // 2. Upload to Storage
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      setUploadedFileUrl(path);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen p-5">
        <div className="bg-white rounded-4xl p-8 max-w-2xl w-full">
          <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
            Add house announcement
          </h3>
          <p className="mb-4 md:mb-8 text-base md:text-xl text-text-secondary">
            Manage your properties with ease
          </p>

          {/* Step Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg md:text-2xl font-bold text-text-primary">
              Step {step}: {stepsHeading[step - 1]}
            </h3>
            <span className="text-xl text-text-secondary">{step}/2</span>
          </div>

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1 */}
            {step === 1 && (
              <>
                <Input
                  label="Title"
                  placeholder="Add title"
                  {...register('title')}
                  error={errors.title?.message}
                  className="text-base md:!text-xl"
                />

                <div className="flex flex-col gap-1">
                  <label className="font-medium text-text-secondary">
                    Details
                  </label>
                  <textarea
                    {...register('details')}
                    placeholder="Details about the house announcement"
                    className="w-full rounded-md border border-gray-300 p-3 text-base md:text-xl h-[160px] focus:outline-none"
                  />
                  {errors.details?.message && (
                    <p className="text-red-500 text-sm">
                      {errors.details.message}
                    </p>
                  )}
                </div>

                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      label="Category"
                      buttonClassName="!w-full"
                      options={categories}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.category && (
                  <p className="text-red-500 text-sm">
                    {errors.category.message}
                  </p>
                )}

                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      label="Priority"
                      buttonClassName="!w-full"
                      options={priorities}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.priority && (
                  <p className="text-red-500 text-sm">
                    {errors.priority.message}
                  </p>
                )}

                <div>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    {...register('file')}
                    className="hidden"
                    id="file-upload"
                    onChange={(e) => {
                      register('file').onChange(e);
                      setUploadedFileUrl(undefined); // Reset upload status on new file selection
                    }}
                  />
                  {!watch('file') || watch('file')?.length === 0 ? (
                    <label htmlFor="file-upload">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                        <div className="flex flex-col gap-4 items-center justify-center">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 13.5V3"
                              stroke="#141414"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20.25 13.5V19.5H3.75V13.5"
                              stroke="#141414"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8.25 6.75L12 3L15.75 6.75"
                              stroke="#141414"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p className="text-sm">
                            Click to choose a file or drag here
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Size limit: 10MB
                          </p>
                        </div>
                      </div>
                    </label>
                  ) : (
                    <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-md border">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                                stroke="#4B5563"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {watch('file')?.[0]?.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(
                                (watch('file')?.[0]?.size || 0) /
                                (1024 * 1024)
                              ).toFixed(2)}{' '}
                              MB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (uploadedFileUrl) {
                              try {
                                await resourceApi.deleteTempFile(
                                  uploadedFileUrl,
                                );
                                toast.success('File removed successfully');
                              } catch (error) {
                                console.error('Failed to delete file:', error);
                                toast.error('Failed to remove file');
                              }
                            }
                            setValue('file', null);
                            setUploadedFileUrl(undefined);
                            // Also clear the input value to allow selecting the same file again
                            const input = document.getElementById(
                              'file-upload',
                            ) as HTMLInputElement;
                            if (input) input.value = '';
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <span className="sr-only">Remove</span>
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>

                      {!uploadedFileUrl ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={handleFileUpload}
                          disabled={isUploading}
                        >
                          {isUploading ? 'Uploading...' : 'Upload File'}
                        </Button>
                      ) : (
                        <div className="flex items-center justify-center p-2 bg-green-50 text-green-700 rounded text-sm font-medium">
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          File Verified & Uploaded
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <>
                <Controller
                  name="audience"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      label="Select Audience"
                      options={audiences}
                      buttonClassName="!w-full text-base md:!text-lg"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.audience && (
                  <p className="text-red-500 text-sm">
                    {errors.audience.message}
                  </p>
                )}

                {/* Conditional House Selector */}
                {(selectedAudience === 'all_residents' ||
                  selectedAudience === 'specific_houses') && (
                  <>
                    <Controller
                      name="houses"
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col gap-1">
                          <label className="font-medium text-text-secondary">
                            Select Houses
                          </label>
                          <select
                            multiple
                            {...field}
                            value={field.value || []}
                            onChange={(e) => {
                              const selectedOptions = Array.from(
                                e.target.selectedOptions,
                                (option) => option.value,
                              );
                              field.onChange(selectedOptions);
                            }}
                            className="w-full rounded-md border border-gray-300 p-3 text-base md:text-lg min-h-[120px] focus:outline-none"
                          >
                            {houseOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500">
                            Hold Ctrl/Cmd to select multiple houses
                          </p>
                        </div>
                      )}
                    />
                    {errors.houses && (
                      <p className="text-red-500 text-sm">
                        {errors.houses.message}
                      </p>
                    )}
                  </>
                )}

                {/* <Controller
                  name="schedule"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      label="Schedule Announcement"
                      options={scheduleOptions}
                      buttonClassName="!w-full"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.schedule && (
                  <p className="text-red-500 text-sm">
                    {errors.schedule.message}
                  </p>
                )} */}

                <Controller
                  name="schedule"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col">
                      <label className="block font-medium text-gray-700 mb-2">
                        Schedule Announcement
                      </label>
                      <input
                        type="datetime-local"
                        {...field}
                        className="w-full p-3 border border-gray-300 rounded-md text-base md:text-lg focus:outline-none"
                      />
                      {errors.schedule && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.schedule.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </>
            )}

            {isError && (
              <p className="text-red-500 text-sm mt-1">
                {error.message === 'Invalid request data'
                  ? 'Please provide valid data'
                  : 'Something went wrong while creating new announcemnt'}
              </p>
            )}

            {/* Navigation Buttons */}
            <div className="pt-6">
              {step < 2 && (
                <Button
                  variant="primary"
                  size="lg"
                  type="button"
                  onClick={nextStep}
                  className="!block w-full"
                >
                  Continue
                </Button>
              )}

              {step === 2 && (
                <div className="">
                  <Button
                    variant="primary"
                    size="lg"
                    type="submit"
                    className="w-full mb-4 !font-bold !text-base md:!text-xl"
                    disabled={isUploading}
                    onClick={() => setAnnouncementStatus('PUBLISHED')}
                  >
                    {isUploading ? 'Uploading...' : 'Publish now'}
                  </Button>
                  <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      type="submit"
                      disabled={isUploading}
                      className="!block w-full !border-[#5761EB] !font-bold !text-base md:!text-xl"
                      onClick={() => setAnnouncementStatus('DRAFT')}
                    >
                      Save as draft
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      type="submit"
                      disabled={isUploading}
                      className="!block w-full !border-[#5761EB] !font-bold !text-base md:!text-xl"
                      onClick={() => setAnnouncementStatus('SCHEDULED')}
                    >
                      Schedule announcement
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </ModalWrapper>
  );
};
